import jQuery from 'jquery';
import _ from 'lodash';
import {fetchRemoteResult,fetchRemoteJson} from "@core/Api/api-util";

const SYMBOL_ENDPOINT = Symbol('endpoint');

const HttpMethods = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE'
};

/**
 * Forces the given option set to use the given method
 * @param {RequestInit} [opts]
 * @param {string} method
 * @return {RequestInit}
 */
const withMethod = (opts, method) => {
    return { ...(opts || {}), method: method };
};

/**
 * Base class used for consuming API endpoints
 */
class ApiBase {
    /**
     * Get the endpoint associated with this ApiBase instance
     * @returns {string}
     */
    get baseEndpoint(){
        return this[SYMBOL_ENDPOINT];
    }

    /**
     * Create an ApiBase instance with the given relative endpoint
     * @param {string} baseEndpoint Relative endpoint
     */
    constructor(baseEndpoint) {
        baseEndpoint = baseEndpoint.trim();
        
        //Normalize our endpoint by ensuring it ends with '/'
        if(baseEndpoint.endsWith('/')){
            baseEndpoint += '/';
        }
        
        this[SYMBOL_ENDPOINT] = baseEndpoint;
    }

    /**
     * @param {string} endpoint Endpoint to send GET request to 
     * @param {RequestInit?} [opts]
     * @returns {Promise<T>}
     */
    async getResult(endpoint, opts){    
        return await this.fetchResult(
            endpoint,
            withMethod(opts, HttpMethods.GET)
        );
    }


    /**
     * @param {string} endpoint Endpoint to send POST request to
     * @param {RequestInit?} [opts]
     * @returns {Promise<T>}
     */
    async postResult(endpoint, opts){
        return await this.fetchResult(
            endpoint,
            withMethod(opts, HttpMethods.POST)
        );
    }
    
    /**
     * @param {string} endpoint Endpoint to send PUT request to
     * @param {RequestInit?} [opts]
     * @returns {Promise<T>}
     */
    async putResult(endpoint, opts){
        return await this.fetchResult(
            endpoint,
            withMethod(opts, HttpMethods.PUT)
        );
    }

    /**
     * @param {string} endpoint Endpoint to send request to
     * @param {RequestInit?} opts
     * @returns {Promise<T>}
     */
    fetchResult(endpoint, opts){
        //Concat only when endpoint param is provided
        const fullEndpoint = this.baseEndpoint + (endpoint ? endpoint : '');
        return fetchRemoteResult(fullEndpoint, opts);
    }

    /**
     * Fetch api POST request, returns response as json. 
     * @param {String} endpoint endpopint url
     * @param {RequestInit} [opts] fetch api options
     * @returns {Promise<T>}
     */
    async postAndFetchJson(endpoint, opts){
        //Concat only when endpoint param is provided
        const fullEndpoint = this.baseEndpoint + (endpoint ? endpoint : ''); 
         
        //Make sure we have the necessary headers.
        opts = { 
            method: HttpMethods.POST, ..._.merge({}, {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                }
            }, (opts || {}))
        };

         return await fetchRemoteJson(
            fullEndpoint, 
            opts
        );
    }

    /**
     * Convert an object to QueryString parameters
     * 
     * @param {object} obj  Object to transform into a set of queryString parameters.
     * @returns {string}    QueryString parameters separated by '&' characters.
     */
    static parameterize(obj){
        //Traditional mode is required for an MVC supported Array format
        const TRADITIONAL = true;
        
        //Proxy to jQuery to make eventual replacement easier
        return jQuery.param(obj, TRADITIONAL);
    }
}

export default ApiBase;